import { useBoolean } from 'ahooks';
import { keyBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  FormTemplate,
  FormTemplateAutomationRuleOption,
  FormTemplateTaskCreationConfig,
} from 'api/form';
import { Button } from 'components/atoms/button';
import { Loading } from 'components/atoms/loading';
import { Switch } from 'components/atoms/switch/switch';
import { Tag } from 'components/atoms/tag';
import { Page } from 'components/molecules/page';
import { BodyShort01, Caption01, ProductiveHeading07 } from 'components/styles';
import {
  useFormAutomationProjectTemplateOptions,
  useFormTemplateUpdateMutation,
} from 'queries/form-template';
import { getRandomColor } from 'utils/get-random-color';
import { FormAutomationConfigurationSlider } from './configuration-slider';
import { FormAutomationModal } from './form-automation-for-projects-modal';

const Wrapper = styled.div`
  width: 928px;
  margin: 50px auto;
`;

const Label = styled.div`
  ${ProductiveHeading07};
`;
const Description = styled.div`
  ${Caption01};
  color: var(--gray-100);
  margin-top: var(--spacing-01);
`;

const ConfigItems = styled.div`
  padding-top: var(--spacing-05);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-06);
`;
const ConfigItem = styled.div`
  display: flex;
  ${BodyShort01};
  gap: var(--spacing-03);
  min-width: 200px;
`;
const ConfigSliderButton = styled(Button)`
  padding: 0;
`;
const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
const PreviewTagWrapper = styled.div`
  margin-left: -2px;
`;

const PreviewTag = styled(Tag)`
  gap: var(--spacing-02);
  margin-right: var(--spacing-02);
`;

export const FormAutomation = ({
  formTemplate,
}: {
  formTemplate: FormTemplate;
}): JSX.Element => {
  const [showConfigSlider, setShowConfigSlider] = useState(false);
  const [taskCreationRoleEnabled, setTaskCreationRoleEnabled] = useState(
    formTemplate?.automation?.ruleEnabled ?? false,
  );

  const [enableFormAutomationModal, { setTrue, setFalse }] = useBoolean(false);
  const { mutateAsync, isLoading: isSaveConfigurationLoading } =
    useFormTemplateUpdateMutation(formTemplate.templateId);
  const { data: projectTemplateOptions, isLoading } =
    useFormAutomationProjectTemplateOptions();

  const isRuleListAvailable =
    (formTemplate?.automation?.ruleList || []).length > 0;

  const handleTaskCreationCustomization = (value) => {
    if (value && !isRuleListAvailable) {
      setShowConfigSlider(true);
      setTaskCreationRoleEnabled(true);
    } else {
      setTaskCreationRoleEnabled(value);
      mutateAsync({
        automation: {
          ...formTemplate.automation,
          ruleEnabled: value,
        },
      });
    }
  };

  const toggleFormAutomation = (
    enableAutomation: boolean,
    updateAllInstances: boolean,
  ) => {
    // If task creation is disabled, disable the task creation under phase as well.
    if (!enableAutomation) {
      setTaskCreationRoleEnabled(false);
    }

    mutateAsync({
      automation: {
        ...formTemplate.automation,
        enabled: enableAutomation,
        ruleEnabled: false,
      },
      updateAllInstance: updateAllInstances,
    });
    setFalse();
  };

  const saveConfiguration = async (
    values: FormTemplateTaskCreationConfig[],
  ) => {
    await mutateAsync(
      {
        automation: {
          ...formTemplate.automation,
          ruleEnabled: true,
          ruleList: values,
        },
      },
      {
        onSuccess: () => {
          setShowConfigSlider(false);
        },
      },
    );
  };

  const closeSlider = () => {
    setShowConfigSlider(false);
    if (!isRuleListAvailable) {
      setTaskCreationRoleEnabled(false);
    }
  };

  const projectPhaseKeyValueMap = useMemo(() => {
    return keyBy(
      (projectTemplateOptions || []).map((phase) => {
        return {
          ...phase,
          projectPhases: keyBy(phase.projectPhases, 'phaseTemplateId'),
        };
      }),
      'projectTemplateId',
    );
  }, [projectTemplateOptions]);

  const defaultRuleValue = useMemo(() => {
    const defaultConfiguration = formTemplate.automation?.ruleList || [];

    if (defaultConfiguration.length) {
      return {
        ruleList: defaultConfiguration.reduce(
          (
            acc: FormTemplateAutomationRuleOption[],
            config: FormTemplateTaskCreationConfig,
          ) => {
            if (!projectPhaseKeyValueMap[config.projectTemplateId]) {
              return acc;
            }

            return [
              ...acc,
              {
                projectTemplateId: {
                  label:
                    projectPhaseKeyValueMap[config.projectTemplateId]
                      ?.projectTemplateName ?? null,
                  value: config.projectTemplateId,
                },
                phaseTemplateId: {
                  label:
                    projectPhaseKeyValueMap[config.projectTemplateId]
                      ?.projectPhases[config.phaseTemplateId]
                      ?.phaseTemplateName ?? null,
                  value: config.phaseTemplateId,
                },
              },
            ];
          },
          [],
        ),
      };
    }

    return {
      ruleList: [
        {
          projectTemplateId: null,
          phaseTemplateId: null,
        },
      ],
    };
  }, [formTemplate.automation?.ruleList, projectPhaseKeyValueMap]);

  return (
    <Page>
      <Page.Content>
        <Wrapper>
          {isLoading ? (
            <LoadingWrapper>
              <Loading />
            </LoadingWrapper>
          ) : (
            <>
              <Label>
                <FormattedMessage id="tasks" />
              </Label>

              <ConfigItems>
                <ConfigItem>
                  <Switch
                    size="small"
                    checked={formTemplate.automation?.enabled ?? false}
                    onClick={setTrue}
                  />
                  <div>
                    <FormattedMessage id="form_templates.automation.task_creation_heading" />
                    <Description>
                      <FormattedMessage id="form_templates.automation.task_creation_description" />
                    </Description>
                  </div>
                </ConfigItem>

                {formTemplate.automation?.enabled ? (
                  <ConfigItem>
                    <Switch
                      size="small"
                      checked={taskCreationRoleEnabled}
                      onChange={handleTaskCreationCustomization}
                    />
                    <div>
                      <div>
                        <FormattedMessage id="form_template.automation.task_configuration_heading" />
                      </div>
                      {isRuleListAvailable ? (
                        <>
                          <PreviewTagWrapper>
                            <PreviewTag
                              color={getRandomColor(
                                (
                                  defaultRuleValue
                                    .ruleList[0] as FormTemplateAutomationRuleOption
                                )?.projectTemplateId?.value,
                              )}
                            >
                              <FormattedMessage
                                id="form_template.automation.rule_config_preview"
                                values={{
                                  projectTemplateName: (
                                    defaultRuleValue
                                      .ruleList[0] as FormTemplateAutomationRuleOption
                                  )?.projectTemplateId?.label,
                                  projectPhaseName: (
                                    defaultRuleValue
                                      .ruleList[0] as FormTemplateAutomationRuleOption
                                  )?.phaseTemplateId?.label,
                                  strong: (chunks) => <strong>{chunks}</strong>,
                                }}
                              />
                            </PreviewTag>
                            {(formTemplate.automation.ruleList || []).length >
                            1 ? (
                              <PreviewTag
                                color={getRandomColor(
                                  formTemplate.automation.ruleList.length,
                                )}
                              >
                                {' '}
                                +{formTemplate.automation.ruleList.length - 1}
                              </PreviewTag>
                            ) : null}
                          </PreviewTagWrapper>
                          <ConfigSliderButton
                            type="link"
                            onClick={() => setShowConfigSlider(true)}
                          >
                            <FormattedMessage id="edit" />
                          </ConfigSliderButton>
                        </>
                      ) : null}
                    </div>
                  </ConfigItem>
                ) : null}
              </ConfigItems>
              <FormAutomationConfigurationSlider
                defaultRuleValue={defaultRuleValue}
                showSlider={showConfigSlider}
                saveConfiguration={saveConfiguration}
                isSaveConfigurationLoading={isSaveConfigurationLoading}
                closeSlider={closeSlider}
                projectPhaseMapping={projectTemplateOptions || []}
              />
              <FormAutomationModal
                isVisible={enableFormAutomationModal}
                closeModal={setFalse}
                toggleFormAutomation={toggleFormAutomation}
                formTemplate={formTemplate}
              />
            </>
          )}
        </Wrapper>
      </Page.Content>
    </Page>
  );
};
